import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../../components/Header'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { trainingGroupsAPI } from '../../config/api'
import { motion } from 'framer-motion'
import 'primeicons/primeicons.css'

interface TrainingGroup {
  id: number
  name: string
  description?: string
  image?: string
  image_url?: string
  link?: string
  members: any[]
  member_count: number
  is_active: boolean
  created_at: string
}

interface Comment {
  id: number
  user: {
    id: number
    username: string
    first_name?: string
    last_name?: string
    avatar?: string
  }
  content: string
  created_at: string
  updated_at: string
}

export default function TrainingGroupsPage() {
  const { theme } = useTheme()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [groups, setGroups] = useState<TrainingGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedGroup, setSelectedGroup] = useState<TrainingGroup | null>(null)
  const [comments, setComments] = useState<Comment[]>([])
  const [commentContent, setCommentContent] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)
  const [loadingComments, setLoadingComments] = useState(false)

  useEffect(() => {
    const fetchGroups = async () => {
      if (!isAuthenticated) {
        setLoading(false)
        setGroups([])
        return
      }

      try {
        setLoading(true)
        const data = await trainingGroupsAPI.getMyGroups()
        
        // Xử lý response
        let groupsArray: TrainingGroup[] = []
        if (Array.isArray(data)) {
          groupsArray = data
        } else if (data && Array.isArray(data.results)) {
          groupsArray = data.results
        } else if (data && Array.isArray(data.data)) {
          groupsArray = data.data
        }
        
        setGroups(groupsArray)
      } catch (error: any) {
        // Error fetching my training groups
        setGroups([])
      } finally {
        setLoading(false)
      }
    }

    fetchGroups()
  }, [isAuthenticated])

  useEffect(() => {
    if (selectedGroup) {
      fetchComments(selectedGroup.id)
    }
  }, [selectedGroup])

  const fetchComments = async (groupId: number) => {
    try {
      setLoadingComments(true)
      const data = await trainingGroupsAPI.getGroupComments(groupId)
      setComments(Array.isArray(data) ? data : [])
    } catch (error) {
      setComments([])
    } finally {
      setLoadingComments(false)
    }
  }

  const handleSubmitComment = async () => {
    if (!selectedGroup || !commentContent.trim() || !isAuthenticated) return

    try {
      setSubmittingComment(true)
      await trainingGroupsAPI.addGroupComment(selectedGroup.id, {
        content: commentContent.trim()
      })
      setCommentContent('')
      await fetchComments(selectedGroup.id)
    } catch (error) {
      // Error submitting comment
    } finally {
      setSubmittingComment(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
      <Header />

      {/* Header Section */}
      <section className="relative py-16 md:py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/homepage-background.png"
            alt="Background"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = '/images/team-photo-1-BB_Ia2hP.jpg'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/60"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-gold-900/20 via-transparent to-gold-900/20"></div>
        </div>
        
        <div className="relative z-10 container mx-auto text-center max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 text-white drop-shadow-2xl">
              <span className="bg-gradient-to-r from-gold-300 via-gold-400 to-gold-300 bg-clip-text text-transparent">
                Nhóm Luyện Tập
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 drop-shadow-lg">
              Tham gia các nhóm luyện tập và kết nối với cộng đồng
            </p>
          </motion.div>
        </div>
      </section>

      {/* Groups Section */}
      <section className={`py-12 md:py-16 px-4 ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
        <div className="container mx-auto max-w-6xl">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400 mx-auto mb-4"></div>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải nhóm luyện tập...</p>
            </div>
          ) : !isAuthenticated ? (
            <div className={`text-center py-20 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-gold-500/10' : 'bg-gold-100'}`}>
                <i className="pi pi-lock text-5xl text-gold-400"></i>
              </div>
              <h3 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Vui lòng đăng nhập
              </h3>
              <p className="text-lg mb-6">Bạn cần đăng nhập để xem các nhóm luyện tập của mình</p>
              <button
                onClick={() => navigate('/login')}
                className="px-8 py-3 bg-gold-gradient text-white font-bold rounded-lg hover:shadow-lg hover:shadow-gold-500/50 transition transform hover:scale-105"
              >
                Đăng nhập
              </button>
            </div>
          ) : groups.length === 0 ? (
            <div className={`text-center py-20 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-gold-500/10' : 'bg-gold-100'}`}>
                <i className="pi pi-users text-5xl text-gold-400"></i>
              </div>
              <h3 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Bạn chưa tham gia nhóm nào
              </h3>
              <p className="text-lg">Bạn chưa tham gia nhóm luyện tập nào. Hãy liên hệ admin để được thêm vào nhóm.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {groups.map((group, index) => {
                // Check if link is YouTube
                const isYouTube = group.link && (group.link.includes('youtube.com') || group.link.includes('youtu.be'))
                
                return (
                  <motion.div
                    key={group.id}
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    whileHover={{ y: -8, scale: 1.02 }}
                    className={`group relative rounded-2xl overflow-hidden border ${
                      theme === 'dark' 
                        ? 'border-gold-500/30 bg-gradient-to-br from-gray-900/90 to-black hover:border-gold-500/50' 
                        : 'border-gold-200 bg-white hover:border-gold-400'
                    } shadow-xl ${theme === 'dark' ? 'shadow-gold-500/10' : 'shadow-gray-200'} hover:shadow-2xl ${theme === 'dark' ? 'hover:shadow-gold-500/20' : 'hover:shadow-gold-300/30'} transition-all duration-300 flex flex-col h-full`}
                  >
                    {/* Banner Image Section - Top */}
                    {(group.image_url || group.image) ? (
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img
                          src={group.image_url || group.image}
                          alt={group.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            // Fallback nếu image không load được
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      </div>
                    ) : (
                      /* Fallback - Avatar nếu không có banner */
                      <div className={`relative aspect-[16/9] ${theme === 'dark' ? 'bg-gradient-to-br from-gold-900/30 to-gold-800/10' : 'bg-gradient-to-br from-gold-100 to-gold-50'}`}>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className={`w-32 h-32 rounded-full flex items-center justify-center ${
                            theme === 'dark' ? 'bg-gold-500/20 border-4 border-gold-500/30' : 'bg-gold-200 border-4 border-gold-300'
                          }`}>
                            <i className="pi pi-users text-5xl text-gold-400"></i>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Content Section */}
                    <div className={`p-5 md:p-6 flex-1 flex flex-col ${theme === 'dark' ? 'bg-gray-900/50' : 'bg-white'}`}>
                      {/* Group Name - Hiển thị dưới ảnh */}
                      <h2 className={`text-xl md:text-2xl font-bold mb-3 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'} line-clamp-2`}>
                        {group.name}
                      </h2>
                      
                      {/* Description */}
                      {group.description && (
                        <p className={`text-sm md:text-base mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} line-clamp-3 flex-1`}>
                          {group.description}
                        </p>
                      )}
                      
                      {/* YouTube Link */}
                      {group.link && isYouTube && (
                        <a
                          href={group.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold mb-4 transition-all ${
                            theme === 'dark'
                              ? 'bg-red-600/20 text-red-400 border border-red-500/30 hover:bg-red-600/30'
                              : 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
                          } transform hover:scale-105 active:scale-95`}
                        >
                          <i className="pi pi-youtube text-xl"></i>
                          <span>Xem trên YouTube</span>
                          <i className="pi pi-external-link text-sm"></i>
                        </a>
                      )}
                      
                      {/* Other Link */}
                      {group.link && !isYouTube && (
                        <a
                          href={group.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold mb-4 transition-all ${
                            theme === 'dark'
                              ? 'bg-gold-500/20 text-gold-400 border border-gold-500/30 hover:bg-gold-500/30'
                              : 'bg-gold-50 text-gold-600 border border-gold-200 hover:bg-gold-100'
                          } transform hover:scale-105 active:scale-95`}
                        >
                          <i className="pi pi-link text-lg"></i>
                          <span className="truncate max-w-[200px]">Tham gia ngay</span>
                          <i className="pi pi-external-link text-sm"></i>
                        </a>
                      )}
                    </div>

                    {/* Footer Section */}
                    <div className={`p-5 md:p-6 border-t ${theme === 'dark' ? 'border-gold-500/20 bg-gray-900/30' : 'border-gold-200 bg-gold-50/30'}`}>
                      {/* Action Button */}
                      <button
                        onClick={() => setSelectedGroup(group)}
                        className={`w-full px-4 py-3 rounded-lg font-semibold border-2 transition-all flex items-center justify-center gap-2 ${
                          theme === 'dark'
                            ? 'border-gold-400 text-gold-400 hover:bg-gold-400/10'
                            : 'border-gold-500 text-gold-600 hover:bg-gold-50'
                        } transform hover:scale-105 active:scale-95`}
                      >
                        <i className="pi pi-comments"></i>
                        <span>Xem bình luận</span>
                      </button>
                    </div>

                    {/* Hover Effect Glow */}
                    <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${
                      theme === 'dark' ? 'bg-gradient-to-br from-gold-500/5 to-transparent' : 'bg-gradient-to-br from-gold-50/50 to-transparent'
                    }`}></div>
                  </motion.div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* Comment Modal */}
      {selectedGroup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setSelectedGroup(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`relative w-full max-w-6xl max-h-[90vh] md:max-h-[85vh] overflow-hidden rounded-xl md:rounded-2xl ${
              theme === 'dark' ? 'bg-gray-900 border border-gold-500/30' : 'bg-white border border-gold-200'
            } shadow-2xl flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with Close Button */}
            <div className={`p-3 md:p-4 border-b ${theme === 'dark' ? 'border-gold-500/20' : 'border-gold-200'} flex items-center justify-between`}>
              <h2 className={`text-lg md:text-2xl font-extrabold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                Bình luận
              </h2>
              <button
                onClick={() => setSelectedGroup(null)}
                className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center ${theme === 'dark' ? 'hover:bg-gray-800 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'} transition-colors`}
              >
                <i className="pi pi-times text-lg md:text-xl"></i>
              </button>
            </div>

            {/* Main Content - Two Column Layout */}
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
              {/* Left Side - Group Information */}
              <div className={`w-full md:w-1/3 md:border-r border-b md:border-b-0 ${theme === 'dark' ? 'border-gold-500/20 bg-gray-900/50' : 'border-gold-200 bg-gold-50/30'} overflow-y-auto custom-scrollbar max-h-[35vh] md:max-h-none`}>
                <div className="p-4 md:p-6">
                  {/* Banner Image */}
                  {(selectedGroup.image_url || selectedGroup.image) ? (
                    <div className="relative aspect-[16/9] rounded-lg md:rounded-xl overflow-hidden mb-4 md:mb-6">
                      <img
                        src={selectedGroup.image_url || selectedGroup.image}
                        alt={selectedGroup.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                    </div>
                  ) : (
                    <div className={`relative aspect-[16/9] rounded-lg md:rounded-xl overflow-hidden mb-4 md:mb-6 ${theme === 'dark' ? 'bg-gradient-to-br from-gold-900/30 to-gold-800/10' : 'bg-gradient-to-br from-gold-100 to-gold-50'} flex items-center justify-center`}>
                      <div className={`w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center ${
                        theme === 'dark' ? 'bg-gold-500/20 border-2 md:border-4 border-gold-500/30' : 'bg-gold-200 border-2 md:border-4 border-gold-300'
                      }`}>
                        <i className="pi pi-users text-3xl md:text-4xl text-gold-400"></i>
                      </div>
                    </div>
                  )}

                  {/* Group Name */}
                  <h3 className={`text-xl md:text-2xl font-bold mb-3 md:mb-4 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                    {selectedGroup.name}
                  </h3>

                  {/* Description */}
                  {selectedGroup.description && (
                    <div className="mb-4 md:mb-6">
                      <h4 className={`text-xs md:text-sm font-semibold mb-2 uppercase tracking-wide ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Mô tả
                      </h4>
                      <p className={`text-xs md:text-base leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {selectedGroup.description}
                      </p>
                    </div>
                  )}

                  {/* Group Stats */}
                  <div className={`p-3 md:p-4 rounded-lg md:rounded-xl ${theme === 'dark' ? 'bg-gray-800/50 border border-gold-500/10' : 'bg-white border border-gold-100'}`}>
                    <div className="flex items-center justify-between mb-2 md:mb-3">
                      <span className={`text-xs md:text-sm font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Thành viên
                      </span>
                      <span className={`text-base md:text-lg font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                        {selectedGroup.member_count || selectedGroup.members?.length || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs md:text-sm font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Bình luận
                      </span>
                      <span className={`text-base md:text-lg font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                        {comments.length}
                      </span>
                    </div>
                  </div>

                  {/* YouTube Link */}
                  {selectedGroup.link && (selectedGroup.link.includes('youtube.com') || selectedGroup.link.includes('youtu.be')) && (
                    <a
                      href={selectedGroup.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`mt-4 md:mt-6 inline-flex items-center justify-center gap-2 w-full px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-lg text-sm md:text-base font-semibold transition-all ${
                        theme === 'dark'
                          ? 'bg-red-600/20 text-red-400 border border-red-500/30 hover:bg-red-600/30'
                          : 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
                      } transform hover:scale-105 active:scale-95`}
                    >
                      <i className="pi pi-youtube text-lg md:text-xl"></i>
                      <span>Xem trên YouTube</span>
                      <i className="pi pi-external-link text-xs md:text-sm"></i>
                    </a>
                  )}

                  {/* Other Link */}
                  {selectedGroup.link && !selectedGroup.link.includes('youtube.com') && !selectedGroup.link.includes('youtu.be') && (
                    <a
                      href={selectedGroup.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`mt-4 md:mt-6 inline-flex items-center justify-center gap-2 w-full px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-lg text-sm md:text-base font-semibold transition-all ${
                        theme === 'dark'
                          ? 'bg-gold-500/20 text-gold-400 border border-gold-500/30 hover:bg-gold-500/30'
                          : 'bg-gold-50 text-gold-600 border border-gold-200 hover:bg-gold-100'
                      } transform hover:scale-105 active:scale-95`}
                    >
                      <i className="pi pi-link text-base md:text-lg"></i>
                      <span>Tham gia ngay</span>
                      <i className="pi pi-external-link text-xs md:text-sm"></i>
                    </a>
                  )}
                </div>
              </div>

              {/* Right Side - Comments */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto p-4 md:p-6 custom-scrollbar">
                  {loadingComments ? (
                    <div className="text-center py-8 md:py-12">
                      <div className="animate-spin rounded-full h-8 md:h-10 w-8 md:w-10 border-b-2 border-gold-400 mx-auto mb-3"></div>
                      <p className={`text-sm md:text-base ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Đang tải bình luận...</p>
                    </div>
                  ) : comments.length === 0 ? (
                    <div className={`text-center py-12 md:py-16 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      <div className={`w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-gold-500/10' : 'bg-gold-100'}`}>
                        <i className="pi pi-comments text-3xl md:text-4xl text-gold-400"></i>
                      </div>
                      <p className="text-base md:text-lg font-semibold mb-2">Chưa có bình luận nào</p>
                      <p className="text-xs md:text-sm">Hãy là người đầu tiên bình luận!</p>
                    </div>
                  ) : (
                    <div className="space-y-3 md:space-y-4">
                      {comments.map((comment) => (
                        <motion.div
                          key={comment.id}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className={`p-3 md:p-4 rounded-lg md:rounded-xl ${theme === 'dark' ? 'bg-gray-800/50 border border-gold-500/10 hover:border-gold-500/30' : 'bg-gray-50 border border-gold-100 hover:border-gold-300'} transition-all`}
                        >
                          <div className="flex items-start gap-3 md:gap-4">
                            {/* Avatar */}
                            <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${theme === 'dark' ? 'bg-gold-500/20 border-2 border-gold-500/30' : 'bg-gold-100 border-2 border-gold-200'}`}>
                              {comment.user.avatar ? (
                                <img
                                  src={comment.user.avatar}
                                  alt={comment.user.username}
                                  className="w-full h-full rounded-full object-cover"
                                />
                              ) : (
                                <i className="pi pi-user text-lg md:text-xl text-gold-400"></i>
                              )}
                            </div>
                            
                            {/* Comment Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 md:mb-2 flex-wrap">
                                <span className={`font-bold text-sm md:text-base ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                  {comment.user.first_name || comment.user.last_name || comment.user.username}
                                </span>
                                <span className={`text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full ${theme === 'dark' ? 'bg-gold-500/10 text-gold-400' : 'bg-gold-100 text-gold-600'}`}>
                                  {formatDate(comment.created_at)}
                                </span>
                              </div>
                              <p className={`text-xs md:text-base leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-wrap break-words`}>
                                {comment.content}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Comment Form - Fixed at Bottom */}
                <div className={`p-4 md:p-6 border-t ${theme === 'dark' ? 'border-gold-500/20 bg-gray-900/50' : 'border-gold-200 bg-white'}`}>
                  {isAuthenticated ? (
                    <div className="space-y-2 md:space-y-3">
                      <textarea
                        value={commentContent}
                        onChange={(e) => setCommentContent(e.target.value)}
                        placeholder="Viết bình luận của bạn..."
                        rows={2}
                        className={`w-full p-3 md:p-4 rounded-lg md:rounded-xl resize-none text-sm md:text-base ${
                          theme === 'dark' 
                            ? 'bg-gray-800 border border-gold-500/20 text-white placeholder-gray-500 focus:border-gold-500/50' 
                            : 'bg-gray-50 border border-gold-200 text-gray-900 placeholder-gray-400 focus:border-gold-400'
                        } focus:outline-none focus:ring-2 focus:ring-gold-400/50 transition-all`}
                      />
                      <button
                        onClick={handleSubmitComment}
                        disabled={!commentContent.trim() || submittingComment}
                        className={`w-full px-4 md:px-6 py-2.5 md:py-3 rounded-lg md:rounded-xl text-sm md:text-base font-semibold transition-all flex items-center justify-center gap-2 ${
                          commentContent.trim() && !submittingComment
                            ? theme === 'dark'
                              ? 'bg-gold-gradient text-white hover:shadow-lg hover:shadow-gold-500/50 transform hover:scale-[1.02]'
                              : 'bg-gold-gradient text-white hover:shadow-lg hover:shadow-gold-400/50 transform hover:scale-[1.02]'
                            : theme === 'dark'
                            ? 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gold-500/10'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gold-200'
                        }`}
                      >
                        {submittingComment ? (
                          <>
                            <i className="pi pi-spin pi-spinner text-sm md:text-base"></i>
                            <span>Đang gửi...</span>
                          </>
                        ) : (
                          <>
                            <i className="pi pi-send text-sm md:text-base"></i>
                            <span>Gửi bình luận</span>
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className={`text-center py-3 md:py-4 px-3 md:px-4 rounded-lg md:rounded-xl ${theme === 'dark' ? 'bg-gray-800/50 border border-gold-500/20' : 'bg-gray-50 border border-gold-200'}`}>
                      <p className={`text-sm md:text-base mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Vui lòng đăng nhập để bình luận
                      </p>
                      <button
                        onClick={() => {
                          setSelectedGroup(null)
                          navigate('/login')
                        }}
                        className="px-4 md:px-6 py-2 bg-gold-gradient text-white text-sm md:text-base font-semibold rounded-lg hover:shadow-lg transition-all transform hover:scale-105"
                      >
                        Đăng nhập
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </main>
  )
}
